import { useLazyGetCardQuery } from '@hts/shared/api';
import { useAppSelector } from '@hts/shared/libs';
import {
  ICategoryItemInput,
  IDefaultFontInput,
  IGetCardDataResponse,
  IGetEditOrderDataResponse,
  IImageDimensionsInput,
  SetReactState,
} from '@hts/shared/types';
import * as Sentry from '@sentry/react';
import { useCallback, useEffect } from 'react';
import { IMusicListState, INewCardState } from '../../types';
import {
  getClearedTextFieldsHashes,
  getInsertedImages,
  getLogoState,
  getSecondaryImageState,
  getTextFieldsState,
  setPreviewUrlState,
} from '../../utils';
import { useCardContext } from './context';

type IBaseCardData = Pick<
  IGetCardDataResponse,
  | 'cardAudios'
  | 'cardFolders'
  | 'cardDetails'
  | 'text_attributes_version'
  | 'secondaryImage'
  | 'logo'
  | 'insertedImages'
  | 'name'
  | 'price'
>;

interface ICardData extends IBaseCardData {
  categories: ICategoryItemInput[];
  music_price: number;
  imageDimensions: IImageDimensionsInput | null;
  cardId: string;
  defaultHebrewFont: IDefaultFontInput;
  defaultEnglishFont: IDefaultFontInput;
}

interface IUseGetCardProps {
  cardId?: string;
  editOrderData?: IGetEditOrderDataResponse;
  toggleActiveCategory: (value: boolean) => void;
  setMusicList: SetReactState<IMusicListState | null>;
  setDraftCheckedMusicId: SetReactState<number | null>;
}

export const useGetCard = ({
  cardId,
  editOrderData,
  setMusicList,
  setDraftCheckedMusicId,
  toggleActiveCategory,
}: IUseGetCardProps) => {
  const draftCard = useAppSelector(({ mainReducer }) => mainReducer.draftCard);

  const setCard = useCardContext((state) => state.setCard);

  const [getCardRequest, cardData] = useLazyGetCardQuery({});

  const getBase64Image = useCallback(async (src: string, callback: (url: string) => void) => {
    const response = await fetch(src);
    const blob = await response.blob();
    const reader = new FileReader();

    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      callback(reader.result as string);
    };
  }, []);

  const getBaseCardState = useCallback((cardData: ICardData) => {
    const {
      cardAudios,
      cardFolders,
      cardDetails,
      text_attributes_version,
      secondaryImage,
      logo,
      insertedImages,
      categories,
      imageDimensions,
      music_price,
      cardId,
      name,
      price,
    } = cardData;

    const newCard: INewCardState = {
      categories,
      imageDimensions,
      music_price,
      cardPrice: price,
      defaultEnglishFont: cardDetails.defaultEnglishFont,
      defaultHebrewFont: cardDetails.defaultHebrewFont,
      should_reverse_images: cardDetails?.should_reverse_images ?? 0,
      allow_user_upload_image: cardDetails?.allow_user_upload_image ?? 0,
      cardId,
      previewUrl: null,
      secondaryImage: getSecondaryImageState(secondaryImage),
      logo: getLogoState(logo),
      fields: getTextFieldsState(cardDetails.cardAttributes, text_attributes_version),
      insertedImages: getInsertedImages(insertedImages),
      watermark: cardDetails.watermark || null,
      name,
    };

    setPreviewUrlState(
      {
        url: cardDetails.fileUrlWatermarked || cardDetails.fileUrl || '',
        id: cardId,
      },
      setCard
    );

    if (cardAudios?.length || cardFolders?.length) {
      newCard.audio_id = '';
      setMusicList({ cardAudios, cardFolders });
    }

    if (cardDetails.has_logo) {
      newCard.has_logo = cardDetails.has_logo;
      newCard.logoDetails = {
        image: null,
        width: 150,
        height: 150,
        posX: 0,
        posY: 0,
      };
    }

    if (cardDetails.second_image) {
      let imageUrl = cardDetails.secondImageFileUrl;

      imageUrl = encodeURI(imageUrl);

      getBase64Image(imageUrl, (url: string) => {
        setCard((prev) => {
          if (!prev) return prev;

          const second_image = prev.second_image;

          return {
            ...prev,
            second_image: second_image ? { ...second_image, url } : second_image,
          };
        });
      });

      newCard.second_image = {
        url: cardDetails.secondImageFileUrl,
        height: cardDetails.second_image_height,
        width: cardDetails.second_image_width,
        posx: cardDetails.second_image_position_x,
        posy: cardDetails.second_image_position_y,
        rotation: cardDetails.second_image_rotation,
      };
    }

    return newCard;
  }, []);

  const getCardStateByOrder = useCallback(async (inputEditOrderData: IGetEditOrderDataResponse) => {
    try {
      const { cardDetails } = inputEditOrderData;

      const newCard = getBaseCardState({
        cardId: cardDetails.card_id.toString(),
        price: inputEditOrderData.price,
        defaultEnglishFont: cardDetails.defaultEnglishFont,
        defaultHebrewFont: cardDetails.defaultHebrewFont,
        cardAudios: inputEditOrderData.cardAudios,
        cardDetails: inputEditOrderData.cardDetails,
        cardFolders: inputEditOrderData.cardFolders,
        categories: inputEditOrderData.categories,
        imageDimensions: inputEditOrderData.mainImageDimensions || null,
        music_price: inputEditOrderData.music_price,
        text_attributes_version: inputEditOrderData.text_attributes_version,
        insertedImages: inputEditOrderData.insertedImages,
        logo: inputEditOrderData.logo,
        secondaryImage: inputEditOrderData.secondaryImage,
        name: inputEditOrderData.name,
      });

      newCard.audio_id = inputEditOrderData.audioId?.toString();
      newCard.orderAudioId = inputEditOrderData.audioId;
      newCard.orderId = inputEditOrderData.orderId;

      setCard(newCard);
      toggleActiveCategory(true);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { source: 'getCardStateByOrder' },
        extra: { inputEditOrderData },
      });

      console.log('Get card state by edit order data error', error);
    }
  }, []);

  const getCardStateByCard = useCallback(
    async (inputCardId: string) => {
      try {
        const cardResponse = await getCardRequest(inputCardId).unwrap();

        const cardDetails = cardResponse.cardDetails;

        const newCard = getBaseCardState({
          cardId: inputCardId,
          price: cardResponse.price,
          name: cardResponse.name,
          defaultEnglishFont: cardDetails.defaultEnglishFont,
          defaultHebrewFont: cardDetails.defaultHebrewFont,
          cardAudios: cardResponse.cardAudios,
          cardDetails: cardResponse.cardDetails,
          cardFolders: cardResponse.cardFolders,
          categories: cardResponse.categories,
          imageDimensions: cardResponse.mainImageDimensions,
          music_price: cardResponse.music_price,
          text_attributes_version: cardResponse.text_attributes_version,
          insertedImages: cardResponse.insertedImages,
          logo: cardResponse.logo,
          secondaryImage: cardResponse.secondaryImage,
        });

        const storageCard = sessionStorage.getItem(inputCardId);

        if (draftCard?.cardAttributes) {
          newCard.fields = newCard.fields.map((field, index) => {
            const newField = newCard.fields[index];

            return {
              ...newField,
              text: draftCard.cardAttributes.at(index)?.text || '',
            };
          });

          if (draftCard.audio_id) {
            newCard.audio_id = String(draftCard.audio_id);
            setDraftCheckedMusicId(draftCard.audio_id);
          }
        } else if (storageCard) {
          const storageCardJSON: INewCardState = JSON.parse(storageCard);

          const { storageFieldsHash, backendFieldsHash } = getClearedTextFieldsHashes(
            newCard.fields,
            storageCardJSON.fields
          );

          if (storageFieldsHash !== backendFieldsHash) {
            newCard.fields = storageCardJSON.fields.map((field, index) => {
              const newField = newCard.fields[index];

              return {
                ...newField,
                changedFontSize: field.changedFontSize,
                text: field.text,
                text_lang: field.text_lang,
              };
            });
          } else {
            sessionStorage.removeItem(inputCardId);
          }
        }

        setCard(newCard);
        // setActiveBgImage(cardDetails.thumbnailFileUrl || '');

        toggleActiveCategory(true);
      } catch (error) {
        Sentry.captureException(error, {
          tags: { source: 'getCardStateByCard' },
          extra: { inputCardId },
        });

        console.log('Get card error', error);
      }
    },
    [draftCard?.audio_id, draftCard?.cardAttributes]
  );

  useEffect(() => {
    if (!cardId) return;

    getCardStateByCard(cardId);
  }, [cardId]);

  useEffect(() => {
    if (!editOrderData) return;

    getCardStateByOrder(editOrderData);
  }, [editOrderData]);

  return { cardData };
};
